import React, { PropsWithChildren, ReactElement, useEffect } from 'react';
import { connect } from 'react-redux';
import Head from 'next/head';
import getConfig from 'next/config';
import { useRouter } from 'next/router';

import displayValues from '../../resources/displayValues.json';
import { trackHotelPage } from '../tracking/FinnAnalytics';
import { translateAmenities } from './HotelDescription';

import { Filters, Hotel, PaleState } from '@/interfaces';
import { NoHotelWarning } from './NoHotelWarning';
import { HotelTopBar } from './HotelTopBar';
import { HotelImages } from './HotelImages';

interface Props {
    title?: string;
    hotel: Hotel;
    filters: Filters;
}

const {
    publicRuntimeConfig: { imgPath },
} = getConfig();

export const HotelLayout: React.FC<PropsWithChildren<Props>> = ({ children, hotel, filters }): ReactElement<Props> => {
    const router = useRouter();

    useEffect((): void => {
        trackHotelPage(hotel);
    }, []);

    const hotelTitle: string = hotel && hotel.id ? `Pakkereiser til ${hotel.name}` : 'Dette hotellet finnes ikke :(';
    const imagePath: string =
        hotel && hotel.id && hotel.imagePaths && hotel.imagePaths.length > 0
            ? `${imgPath}1280w${hotel.imagePaths[0]}`
            : 'https://www.finn.no/assets/finn-logo-large.png';
    const fullDescription =
        hotel && hotel.id
            ? `${hotel.name}, ${hotel.destination}, ${hotel.region}, ${hotel.country}
        ${hotel.rating} stjerne
        ${translateAmenities(hotel.facilities, displayValues.amenities.facilities)}
        ${translateAmenities(hotel.activities, displayValues.amenities.activities)}
        ${hotel.distanceToBeach}m fra strand • ${hotel.distanceToCity}m fra sentrum`
            : 'Dette hotellet finnes ikke :(';

    const hotelURL: string =
        hotel && hotel.id
            ? `https://www.finn.no${router.basePath}/hotell?hotellId=${hotel.id}`
            : 'https://www.finn.no/reise/pakkereiser/resultat';

    return (
        <>
            <Head>
                <title key="title">{hotelTitle}</title>
                <meta name="description" content={fullDescription} />
                <meta name="robots" content="noindex" />
                <meta property="og:description" content={fullDescription} />
                <meta property="og:title" content={hotelTitle} />
                <meta property="og:image" content={imagePath} />
                <meta property="og:url" content={hotelURL} />
                <link rel="canonical" href={hotelURL} />
            </Head>
            <div className="pageholder" data-replace-main-section>
                <div className="grid">
                    {hotel.id ? (
                        <div className="u-mh16">
                            <HotelTopBar hotelName={hotel.name} filters={filters} />
                            <HotelImages hotelId={hotel.id} hotelImages={hotel.imagePaths} />
                            <div className="grid__unit u-size1of1">{children}</div>
                        </div>
                    ) : (
                        <>
                            <HotelTopBar hotelName={' '} filters={filters} />
                            <NoHotelWarning filters={filters} />
                        </>
                    )}
                </div>
            </div>
        </>
    );
};

export default connect(({ hotel, filters }: PaleState): Props => ({ hotel, filters }))(HotelLayout);
